// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * ONBOARDING MODAL — Deployment 13
 *
 * 4-step modal shown ONCE on first login after registration.
 * - State stored on MongoDB user document (`onboarding_completed: true`).
 * - NEVER uses localStorage for the completion flag.
 * - Step 2 (RAL → net) calculation is 100% client-side: ZERO network.
 * - RAL value is NEVER persisted (DB / localStorage / calculator_history).
 * - Standalone component — does NOT reuse CalcolatoriPage hooks.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standalone IRPEF calculation per Deployment 13 spec — DO NOT call shared
// calc helpers (which may import non-essential modules).
function computeNetMonthly(ral) {
  const r = Number(ral) || 0;
  if (r <= 0) return 0;
  const inps = r * 0.0919;
  const taxable = r - inps;
  let irpef;
  if (taxable <= 8500) irpef = 0;
  else if (taxable <= 15000) irpef = Math.max(0, taxable * 0.23 - 1955);
  else if (taxable <= 28000) irpef = taxable * 0.23;
  else if (taxable <= 50000) irpef = 6440 + (taxable - 28000) * 0.33;
  else irpef = 13700 + (taxable - 50000) * 0.43;
  return (taxable - irpef) / 12;
}

const fmtEur = (n) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(Math.round(n));

const GOALS = [
  { id: 'risparmio',    emoji: '💰', label: 'Risparmiare di più' },
  { id: 'investimento', emoji: '📈', label: 'Iniziare a investire' },
  { id: 'previdenza',   emoji: '🏦', label: 'Pianificare la pensione' },
  { id: 'tutto',        emoji: '🎯', label: 'Capire tutto' },
];

const RECO = {
  risparmio:    { text: 'Inizia dal Livello 1 e impara a costruire il tuo budget.',           cta: 'Inizia ora →',          path: '/percorsi' },
  investimento: { text: 'Scopri come far crescere i tuoi risparmi con gli investimenti.',     cta: 'Inizia ora →',          path: '/percorsi' },
  previdenza:   { text: 'Pianifica il tuo futuro previdenziale con gli strumenti giusti.',   cta: 'Scopri la previdenza →', path: '/calcolatori/pensione-inps' },
  tutto:        { text: "Parti dall'inizio: ogni livello ti darà strumenti concreti.",        cta: 'Inizia dal Livello 1 →', path: '/percorsi' },
};

const PREMIUM_RECO = { text: 'Sblocca tutti i contenuti L3/L4 e i calcolatori avanzati.', cta: 'Vai ai contenuti avanzati →', path: '/percorsi' };

const OnboardingModal = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [ral, setRal] = useState('');
  const [goal, setGoal] = useState(null);
  const [savingGoal, setSavingGoal] = useState(false);
  const [lessonsReady, setLessonsReady] = useState(false);

  const ralNum = Number(ral) || 0;
  const netMonthly = useMemo(() => computeNetMonthly(ralNum), [ralNum]);

  // Show modal only when MongoDB flag onboarding_completed is explicitly false.
  useEffect(() => {
    if (!user) return;
    if (user.onboarding_completed === false) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Probe lessons availability for step 4 (do not block earlier steps).
  useEffect(() => {
    if (step !== 4) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setLessonsReady(Array.isArray(data) && data.length > 0);
        }
      } catch {/* keep spinner */}
    })();
    return () => { cancelled = true; };
  }, [step]);

  // Mark onboarding completed on MongoDB. Never blocks UI.
  const markCompleted = async (extra = {}) => {
    try {
      await fetch(`${API_URL}/api/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onboarding_completed: true, ...extra }),
      });
    } catch {/* offline-safe: server-side migration ensures only this user */}
    if (refreshUser) refreshUser();
  };

  const close = () => {
    setVisible(false);
    markCompleted();
  };

  const goNext = () => setStep(s => Math.min(4, s + 1));
  const goBack = () => setStep(s => Math.max(1, s - 1));

  // Step 3 — selecting a goal: persist immediately as preference (no PII).
  const selectGoal = async (g) => {
    setGoal(g);
    setSavingGoal(true);
    try {
      await fetch(`${API_URL}/api/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onboarding_goal: g }),
      });
    } catch {/* non-blocking */}
    setSavingGoal(false);
    goNext();
  };

  // Step 4 CTA — close + redirect.
  const ctaStep4 = () => {
    const isPrem = (user?.tier === 'premium');
    const reco = isPrem ? PREMIUM_RECO : (RECO[goal] || RECO.tutto);
    setVisible(false);
    markCompleted();
    navigate(reco.path);
  };

  if (!visible) return null;

  const StepIndicator = () => (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }} data-testid="onboarding-step-indicator">
      {[1, 2, 3, 4].map(n => (
        <div key={n} aria-label={`step-${n}`}
          style={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700,
            background: n <= step ? '#C9A84C' : 'rgba(255,255,255,0.06)',
            color: n <= step ? '#3A2800' : '#888780',
            border: n === step ? '2px solid #E8E8E4' : 'none',
          }}>
          {n === 1 ? '①' : n === 2 ? '②' : n === 3 ? '③' : '④'}
        </div>
      ))}
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        data-testid="onboarding-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          onClick={e => e.stopPropagation()}
          style={{ background: '#1A1A18', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 20, maxWidth: 480, width: '100%', padding: '32px 28px', position: 'relative' }}
        >
          <button onClick={close}
            data-testid="onboarding-close"
            aria-label="Chiudi onboarding"
            style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', cursor: 'pointer', color: '#888780', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%' }}>
            <X className="w-4 h-4" />
          </button>

          <StepIndicator />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="onboarding-step-1">
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#E8E8E4', textAlign: 'center', marginBottom: 12 }}>Benvenuto su SAPIENS 👋</h2>
                <p style={{ color: '#888780', fontSize: 14, lineHeight: 1.7, textAlign: 'center', marginBottom: 24 }}>
                  La piattaforma italiana di educazione finanziaria. In 3 minuti scopri quanto potresti guadagnare in più ogni mese.
                </p>
                <button onClick={goNext} data-testid="onboarding-cta-step-1"
                  style={{ width: '100%', padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14, background: '#C9A84C', color: '#3A2800', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  Inizia <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="onboarding-step-2">
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#E8E8E4', textAlign: 'center', marginBottom: 16 }}>Quanto ti rimane davvero in busta?</h2>
                <label style={{ display: 'block', color: '#888780', fontSize: 13, marginBottom: 6 }}>La tua RAL annua (€)</label>
                <input
                  type="number"
                  value={ral}
                  onChange={e => setRal(e.target.value)}
                  placeholder="es. 30000"
                  min="0"
                  data-testid="onboarding-ral-input"
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 10, background: '#252523', border: '1px solid rgba(255,255,255,0.08)', color: '#E8E8E4', fontSize: 16, outline: 'none', marginBottom: 16 }}
                />
                {ralNum > 0 && (
                  <div style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.3)', borderRadius: 12, padding: 16, marginBottom: 8, textAlign: 'center' }} data-testid="onboarding-net-result">
                    <p style={{ color: '#888780', fontSize: 12, marginBottom: 4 }}>Il tuo netto stimato:</p>
                    <p style={{ color: '#3D7A52', fontSize: 28, fontWeight: 700 }}>€{fmtEur(netMonthly)}/mese</p>
                  </div>
                )}
                <p style={{ color: '#888780', fontSize: 12, textAlign: 'center', margin: '12px 0 20px' }}>
                  Vuoi ottimizzare questo numero? <span style={{ color: '#E8E8E4' }}>SAPIENS ti insegna come.</span>
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={goBack} data-testid="onboarding-back-step-2"
                    style={{ flex: '0 0 auto', padding: '12px 18px', borderRadius: 10, fontSize: 14, background: 'transparent', color: '#888780', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Indietro
                  </button>
                  <button
                    onClick={goNext}
                    disabled={!(ralNum > 0)}
                    data-testid="onboarding-cta-step-2"
                    style={{ flex: 1, padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14, background: ralNum > 0 ? '#C9A84C' : '#3A2800', color: ralNum > 0 ? '#3A2800' : '#888780', border: 'none', cursor: ralNum > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: ralNum > 0 ? 1 : 0.6 }}>
                    Continua <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="onboarding-step-3">
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#E8E8E4', textAlign: 'center', marginBottom: 20 }}>Qual è il tuo obiettivo principale?</h2>
                <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                  {GOALS.map(g => (
                    <button key={g.id}
                      onClick={() => selectGoal(g.id)}
                      disabled={savingGoal}
                      data-testid={`onboarding-goal-${g.id}`}
                      style={{
                        padding: '14px 16px', borderRadius: 12,
                        background: goal === g.id ? 'rgba(201,168,76,0.12)' : '#252523',
                        border: `1px solid ${goal === g.id ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
                        color: '#E8E8E4', cursor: savingGoal ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 12,
                        textAlign: 'left', fontSize: 14, fontWeight: 500,
                        transition: 'all 0.2s',
                      }}>
                      <span style={{ fontSize: 22 }}>{g.emoji}</span>
                      <span>{g.label}</span>
                    </button>
                  ))}
                </div>
                <button onClick={goBack} data-testid="onboarding-back-step-3"
                  style={{ width: '100%', padding: '10px 18px', borderRadius: 10, fontSize: 13, background: 'transparent', color: '#888780', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <ArrowLeft className="w-3.5 h-3.5" /> Indietro
                </button>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} data-testid="onboarding-step-4">
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#E8E8E4', textAlign: 'center', marginBottom: 16 }}>Il tuo percorso consigliato</h2>
                {!lessonsReady ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }} data-testid="onboarding-loading">
                    <div className="w-10 h-10 border-4 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
                    <p style={{ color: '#888780', fontSize: 13, marginTop: 12 }}>Caricamento contenuti…</p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const isPrem = (user?.tier === 'premium');
                      const reco = isPrem ? PREMIUM_RECO : (RECO[goal] || RECO.tutto);
                      return (
                        <>
                          <p style={{ color: '#E8E8E4', fontSize: 14, lineHeight: 1.7, textAlign: 'center', marginBottom: 24 }} data-testid="onboarding-reco-text">
                            {reco.text}
                          </p>
                          <button onClick={ctaStep4} data-testid="onboarding-cta-step-4"
                            style={{ width: '100%', padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14, background: '#C9A84C', color: '#3A2800', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                            {reco.cta}
                          </button>
                        </>
                      );
                    })()}
                    <button onClick={goBack} data-testid="onboarding-back-step-4"
                      style={{ width: '100%', padding: '10px 18px', borderRadius: 10, fontSize: 13, background: 'transparent', color: '#888780', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <ArrowLeft className="w-3.5 h-3.5" /> Indietro
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingModal;
